/**
 * useResultReveal — AS-11 completion-number fade-up orchestrator.
 *
 * The completion result rows already ENTER through ResultStack's
 * `result-row-enter` TransitionGroup (the box arrives + the per-row
 * spring decay rides the W12-δ cascade). AS-11 adds a second, finer beat
 * ON TOP of that register: the VALUE digits reveal via a brief fade-up
 * once the row has landed, so the number "lifts in" rather than being
 * present the instant the row paints.
 *
 * This composable is the timing OWNER for that fade-up. It does NOT own
 * the digit's value-damping (that is glass-ui's `AnimatedDigit`, consumed
 * in ResultStack's `#value` slot) and it does NOT own the row slide (that
 * is the TransitionGroup). It owns ONLY the per-row reveal GATE + the
 * per-row stagger DELAY, both keyed off the SAME `allComplete` rising
 * edge the row cascade reads, so the value fade-up rhymes with — and
 * trails — the row arrival on one shared clock.
 *
 * Mechanism: glass-ui's `useStagger` (the same primitive ResultStack's
 * colour-tint cascade already rides) fans the reveal across the visible
 * rows at one `MOTION_STAGGER_MS.default` beat per row, lead-in by one
 * beat so the value lifts just after the row seats. `revealedFor(index)`
 * is the per-row boolean the `.as11-value` host binds; `delayFor(index)`
 * is the CSS-var delay so the fade-up keyframe staggers without a second
 * JS clock. Under `prefers-reduced-motion` the consumer's `@media`
 * bracket collapses the fade-up to an instant state — the reveal gate
 * still flips so the numbers are present, only the motion is dropped.
 *
 * Sibling pattern: ResultStack's existing `useStagger` tint cascade (same
 * `allComplete` edge, same stagger token). This composable lifts the
 * reveal-timing concern out of the SFC so the AS-11 fade-up is a named,
 * testable orchestrator rather than inline template state.
 */

import { computed, watch, type ComputedRef } from "vue";
import { useStagger } from "@mkbabb/glass-ui/motion-core";

import { MOTION_STAGGER_MS } from "@design";

export interface UseResultRevealOptions {
    /**
     * Reactive count of visible result rows — the stagger fans one reveal
     * beat per row. ResultStack passes `registry.visible.value.length`.
     */
    rowCount: () => number;
    /**
     * Rising-edge gate — the reveal cascade starts when this flips true
     * (the completion edge: `useMetricStateMachine.allComplete`) and
     * resets when it falls (a re-test). Kept as a getter so the consumer
     * forwards its own reactive source without this composable reaching
     * into the state machine itself.
     */
    active: () => boolean;
}

export interface UseResultReveal {
    /** Per-row reveal gate — true once that row's value should fade up. */
    revealedFor: (index: number) => boolean;
    /**
     * Per-row fade-up delay (ms) as a CSS-friendly string for the
     * `--as11-reveal-delay` custom property. Staggers the keyframe so the
     * values lift in sequence off ONE clock — no second JS scheduler.
     */
    delayFor: (index: number) => string;
}

export function useResultReveal(
    options: UseResultRevealOptions,
): UseResultReveal {
    const rows = computed(() => Math.max(0, options.rowCount()));

    // The same cadence the row-enter colour-tint cascade rides: one beat
    // per row, lead-in by one beat so the value fade-up trails the row
    // seating rather than racing it. `immediate: false` so the cascade
    // only runs on the completion rising edge, never at cold mount.
    const stagger = useStagger({
        items: rows.value,
        delayMs: MOTION_STAGGER_MS.default,
        initialDelayMs: MOTION_STAGGER_MS.default,
        immediate: false,
    });

    watch(
        () => options.active(),
        (now, prev) => {
            if (now && !prev) stagger.start();
            else if (!now && prev) stagger.reset();
        },
        { immediate: true },
    );

    // Per-row CSS delay — the keyframe's own stagger. The fade-up rides
    // the `result-row-enter` register, so the JS reveal gate flips the
    // value visible and the CSS delay sequences the lift; one clock, two
    // expressions (gate + keyframe-delay), no second scheduler.
    const delays: ComputedRef<string[]> = computed(() =>
        Array.from(
            { length: rows.value },
            (_, i) => `${i * MOTION_STAGGER_MS.default}ms`,
        ),
    );

    return {
        revealedFor: (index: number) => stagger.revealed.value[index] ?? false,
        delayFor: (index: number) => delays.value[index] ?? "0ms",
    };
}
